/**
 * TeamProvider is the single source of truth for the currently selected team.
 *
 * Responsibilities:
 * - Derives the active `teamId` from the authenticated user (`user.current_team_id`).
 * - Loads and caches the current team entity using React Query.
 * - Exposes team state and query status to the rest of the application.
 * - Updates authorization context when team data is loaded.
 *
 * Behavior:
 * - If the user has no selected team (`teamId === null`), the provider is in `idle` state
 *   and returns `team: null`.
 * - When `teamId` exists, the `team` query is enabled and fetches team details.
 * - Uses `keepPreviousData` to keep query stability across teamId changes,
 *   but `safeTeam` ensures we never expose stale team objects to consumers.
 *   (i.e., when switching teams, we do not return the old team while the new one is loading).
 * - `resetTeam()` clears the cached team query (used on logout / impersonation / session reset).
 * - `refetchTeam()` allows consumers to refresh current team data manually.
 *
 * Notes:
 * - The query key includes `teamId`, ensuring correct caching and isolation per team.
 * - The provider intentionally exposes derived flags (`status`, `isLoading`, etc.)
 *   so consumers do not rely on React Query internals directly.
 */
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import React, { createContext, useCallback, useContext, useMemo } from "react";

import { Team, TeamResponse } from "~/api/Api";
import { apiClient } from "~/api/ApiClient";
import { queryKeys } from "~/api/queryKeys";
import { Authorizations } from "~/api/types";
import { EXPECTS_FORBIDDEN_META } from "~/configs/queryClient";
import { useAuth } from "~/contexts/AuthProvider";
import { tryCatch } from "~/helpers/tryCatch";

type TeamStatus = "idle" | "pending" | "success" | "error";

type TeamContextValue = {
  teamId: number | null;
  team: Team | null;
  authorizations: Authorizations | null;

  status: TeamStatus;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  error: unknown;

  resetTeam: () => void;
  refetchTeam: () => Promise<void>;
};

type TeamQueryData = {
  team: Team | null;
  authorizations: Authorizations | null;
};

type TeamResponseWithAuth = TeamResponse & {
  authorizations?: Authorizations;
};

const TeamContext = createContext<TeamContextValue | null>(null);

export function TeamProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  const { user, mfaRequired } = useAuth();

  const teamId = user?.current_team_id ?? null;

  const { data, status, isLoading, isFetching, isError, error, refetch } =
    useQuery<TeamQueryData>({
      queryKey: queryKeys.team(teamId),
      enabled: !!teamId && !mfaRequired,
      queryFn: async () => {
        const headers = { "Authorize-Member-Actions": "leave, update" };

        const result = await tryCatch(
          apiClient.teams.teamsDetail(teamId as number, { headers }),
        );

        if (result.error) throw result.error;

        const payload = (result.data?.data ??
          null) as TeamResponseWithAuth | null;

        return {
          team: payload?.team ?? null,
          authorizations: payload?.authorizations ?? null,
        };
      },
      // Reread on returning to the tab: the billing state may have changed
      // meanwhile (keepPreviousData keeps the page on screen while it does).
      refetchOnWindowFocus: true,
      retry: 1,
      placeholderData: keepPreviousData,
      // A refused team is handled by the guards, not by SubscriptionSync.
      meta: EXPECTS_FORBIDDEN_META,
    });

  // IMPORTANT:
  // if teamId === null → team must be null
  // if teamId changed → while new team is loading, do not return old team
  const safeTeam =
    teamId == null ? null : data?.team?.id === teamId ? data.team : null;

  // Mirror the same guard as safeTeam: only expose authorizations once the
  // loaded data actually belongs to the current teamId.
  const teamAuthorizations =
    teamId == null || data?.team?.id !== teamId
      ? null
      : (data?.authorizations ?? null);

  const derivedStatus: TeamStatus =
    teamId == null ? "idle" : (status as TeamStatus);

  const resetTeam = useCallback(() => {
    queryClient.removeQueries({ queryKey: ["team"] });
  }, [queryClient]);

  const refetchTeam = useCallback(async () => {
    await refetch();
  }, [refetch]);

  const value = useMemo<TeamContextValue>(
    () => ({
      teamId,
      team: safeTeam,
      authorizations: teamAuthorizations,

      status: derivedStatus,
      isLoading: teamId != null ? isLoading : false,
      isFetching: teamId != null ? isFetching : false,
      isError: teamId != null ? isError : false,
      error: teamId != null ? (error ?? null) : null,

      resetTeam,
      refetchTeam,
    }),
    [
      teamId,
      safeTeam,
      derivedStatus,
      isLoading,
      isFetching,
      isError,
      error,
      resetTeam,
      refetchTeam,
    ],
  );

  return <TeamContext.Provider value={value}>{children}</TeamContext.Provider>;
}

export function useTeam() {
  const ctx = useContext(TeamContext);
  if (!ctx) throw new Error("useTeam must be used within TeamProvider");

  return ctx;
}
